import React, { useState, useEffect } from "react";
import { GraduationCap, Sparkles, Award, Calendar, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Clock, X, BookOpen } from "lucide-react";

export default function TutorCard({ tutor }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState(false);

  const subjectColors = {
    Math: "bg-gradient-to-r from-blue-500 to-cyan-400 text-white border-0 dark:from-blue-700 dark:to-cyan-600",
    English: "bg-gradient-to-r from-rose-500 to-pink-400 text-white border-0 dark:from-red-500 dark:to-pink-600",
    History: "bg-gradient-to-r from-orange-500 to-amber-400 text-white border-0 dark:from-orange-600 dark:to-yellow-500",
    Biology: "bg-gradient-to-r from-emerald-500 to-lime-400 text-white border-0 dark:from-green-600 dark:to-lime-500",
    Chemistry: "bg-gradient-to-r from-pink-500 to-fuchsia-400 text-white border-0 dark:from-pink-600 dark:to-fuchsia-500",
    Physics: "bg-gradient-to-r from-indigo-500 to-sky-400 text-white border-0 dark:from-indigo-600 dark:to-sky-500",
    "Computer Science": "bg-gradient-to-r from-purple-500 to-violet-400 text-white border-0 dark:from-purple-600 dark:to-violet-500"
  };

  useEffect(() => {
    if (showCalendar && tutor.availability) {
      generateAvailabilityFromTutor();
    }
  }, [showCalendar, currentMonth, tutor.availability]);

  const generateAvailabilityFromTutor = () => {
    setLoading(true);
    try {
      const weeklyAvailability = tutor.availability || {};
      const generatedAvailability = {};
      
      const { year, month } = getDaysInMonth(currentMonth);
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      
      const dayNameToNumber = {
        'Sunday': 0,
        'Monday': 1,
        'Tuesday': 2,
        'Wednesday': 3,
        'Thursday': 4,
        'Friday': 5,
        'Saturday': 6
      };
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        const dayOfWeek = date.getDay();
        
        if (date >= new Date().setHours(0, 0, 0, 0)) {
          const dayName = Object.keys(dayNameToNumber).find(
            name => dayNameToNumber[name] === dayOfWeek
          );
          
          if (dayName && weeklyAvailability[dayName] && weeklyAvailability[dayName].length > 0) {
            const dateKey = formatDateKey(year, month, day);
            generatedAvailability[dateKey] = weeklyAvailability[dayName];
          }
        }
      }
      
      setAvailability(generatedAvailability);
    } catch (error) {
      console.error('Error generating availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimeSlotClick = async (timeSlot) => {
    const bookingData = {
      selectedTutor: tutor.id,
      tutorName: tutor.name,
      selectedDate: new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      }),
      selectedTime: timeSlot,
      preSelectedSubjects: tutor.subjects
    };
    
    sessionStorage.setItem('bookingData', JSON.stringify(bookingData));
    window.location.href = '/bookingsform';
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const formatDateKey = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const isDateAvailable = (year, month, day) => {
    const dateKey = formatDateKey(year, month, day);
    return availability[dateKey] && availability[dateKey].length > 0;
  };

  const getMonthName = (date) => {
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    setSelectedDate(null);
  };

  const prevMonth = () => {
    const prevMonthDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
    const today = new Date();
    if (prevMonthDate.getMonth() >= today.getMonth() || prevMonthDate.getFullYear() > today.getFullYear()) {
      setCurrentMonth(prevMonthDate);
      setSelectedDate(null);
    }
  };

  const handleDateClick = (day) => {
    const { year, month } = getDaysInMonth(currentMonth);
    const dateKey = formatDateKey(year, month, day);
    if (isDateAvailable(year, month, day)) {
      setSelectedDate(dateKey);
    }
  };

  const calculateTotalSlots = () => {
    if (!tutor.availability || typeof tutor.availability !== 'object') return 0;
    return Object.values(tutor.availability).reduce((sum, slots) => {
      return sum + (Array.isArray(slots) ? slots.length : 0);
    }, 0);
  };

  const totalAvailableSlots = calculateTotalSlots();

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
    const days = [];
    const today = new Date();
    const isCurrentMonth = today.getMonth() === month && today.getFullYear() === year;

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="h-12"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = formatDateKey(year, month, day);
      const isAvailable = isDateAvailable(year, month, day);
      const isPast = isCurrentMonth && day < today.getDate();
      const isSelected = selectedDate === dateKey;

      days.push(
        <button
          key={day}
          onClick={() => !isPast && handleDateClick(day)}
          disabled={!isAvailable || isPast || loading}
          className={`h-12 rounded-lg font-semibold transition-all ${
            isSelected
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 dark:from-red-500 dark:to-orange-500 text-white shadow-lg scale-110'
              : isAvailable && !isPast
              ? 'bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-700 dark:to-emerald-700 text-green-700 dark:text-green-200 hover:from-green-200 hover:to-emerald-200 dark:hover:from-green-600 dark:hover:to-emerald-600 border-2 border-green-300 dark:border-green-600 hover:scale-105'
              : isPast
              ? 'bg-gray-100 dark:bg-zinc-800 text-gray-400 dark:text-zinc-600 cursor-not-allowed'
              : 'bg-gray-50 dark:bg-zinc-800 text-gray-300 dark:text-zinc-600 cursor-not-allowed'
          }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  return (
    <>
      <div className="hover:-translate-y-2 transition-transform duration-300">
        <div className="overflow-hidden border-3 border-gray-200 hover:border-blue-400 transition-all duration-300 hover:shadow-2xl group bg-white dark:bg-zinc-900 dark:border-zinc-700 dark:hover:border-red-500 dark:shadow-red-700/30 rounded-2xl">
          
          {/* Card Image */}
          <div className="relative h-72 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-zinc-800 dark:to-zinc-900 overflow-hidden">
            {tutor.photo_url && !imageError ? (
              <img 
                src={tutor.photo_url} 
                alt={tutor.name || "Tutor"}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                onError={() => {
                  console.log(`Image failed to load: ${tutor.photo_url}`);
                  setImageError(true);
                }}
                onLoad={() => setImageError(false)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-400 via-purple-400 to-pink-400 dark:from-red-500 dark:via-orange-500 dark:to-yellow-500">
                <div className="w-32 h-32 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <GraduationCap className="w-16 h-16 text-white" />
                </div>
              </div>
            )}

            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="w-12 h-12 bg-white/90 dark:bg-zinc-800/80 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Award className="w-6 h-6 text-blue-600 dark:text-orange-400" />
              </div>
            </div>
          </div>

          {/* Card Content */}
          <div className="p-6">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-zinc-100 mb-3 group-hover:text-blue-600 dark:group-hover:text-red-400 transition-colors">
              {tutor.name || "Tutor Name"}
            </h3>

            {(tutor.current_school || tutor.education) && (
              <div className="mb-4 pb-4 border-b border-gray-200 dark:border-zinc-700 space-y-3">

                {/* Current School */}
                {tutor.current_school && (
                  <div className="flex items-start gap-3 text-gray-700 dark:text-zinc-200">
                    <div className="w-9 h-9 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-red-500/20 dark:to-orange-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <GraduationCap className="w-4 h-4 text-blue-600 dark:text-red-400" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-400 dark:text-zinc-400">
                        Current School
                      </p>
                      <p className="text-sm font-semibold leading-snug">
                        {tutor.current_school}
                      </p>
                    </div>
                  </div>
                )}

                {/* Education */}
                {tutor.education && (
                  <div className="flex items-start gap-3 text-gray-600 dark:text-zinc-300">
                    <div className="w-9 h-9 bg-gradient-to-br from-emerald-100 to-lime-100 dark:from-emerald-500/20 dark:to-lime-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Award className="w-4 h-4 text-emerald-600 dark:text-lime-400" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-400 dark:text-zinc-400">
                        Education
                      </p>
                      <p className="text-sm leading-snug">
                        {tutor.education}
                      </p>
                    </div>
                  </div>
                )}

                {/* Availability */}
                <div className="flex items-start gap-3 text-gray-600 dark:text-zinc-300">
                  <div className="w-9 h-9 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-500/20 dark:to-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-4 h-4 text-green-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-400 dark:text-zinc-400">
                      Availability
                    </p>
                    <p className="text-sm font-semibold leading-snug flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 dark:bg-emerald-400 rounded-full animate-pulse"></div>
                      {totalAvailableSlots} Slots/Week
                    </p>
                  </div>
                </div>

              </div>
            )}

            <div className="flex flex-wrap gap-2 mb-4">
              {tutor.subjects && Array.isArray(tutor.subjects) && tutor.subjects.length > 0 ? (
                tutor.subjects.map((subject, idx) => (
                  <span key={idx} className={`${subjectColors[subject] || 'bg-gray-500 text-white'} font-semibold shadow-md px-3 py-1.5 rounded-full text-sm`}>
                    {subject}
                  </span>
                ))
              ) : (
                <span className="bg-gray-500 text-white font-semibold shadow-md px-3 py-1.5 rounded-full text-sm">
                  General Subjects
                </span>
              )}
            </div>

            {tutor.experience && (
              <div className="mb-4">
                <p className={`text-gray-600 dark:text-zinc-300 leading-relaxed ${!isExpanded ? 'line-clamp-3' : ''}`}>
                  {tutor.experience}
                </p>

                {tutor.experience.length > 150 && (
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="mt-2 text-blue-600 dark:text-red-400 hover:text-blue-700 dark:hover:text-red-500 font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all"
                  >
                    {isExpanded ? (
                      <>
                        Show Less <ChevronUp className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        Read More <ChevronDown className="w-4 h-4" />
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            <button 
              onClick={() => setShowCalendar(true)}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 dark:from-red-500 dark:to-orange-500 hover:from-blue-700 hover:to-purple-700 dark:hover:from-red-600 dark:hover:to-orange-600 text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              <Calendar className="w-5 h-5" />
              View Schedule
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Modal */}
      {showCalendar && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => {
            setShowCalendar(false);
            setSelectedDate(null);
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn"
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-red-600 dark:to-orange-600 text-white p-6 rounded-t-3xl z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Calendar className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold">{tutor.name}'s Schedule</h2>
                    <p className="text-blue-100 dark:text-orange-100">Select a date to view available time slots</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowCalendar(false);
                    setSelectedDate(null);
                  }}
                  className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-red-500"></div>
                  <p className="mt-4 text-gray-600 dark:text-zinc-300">Loading availability...</p>
                </div>
              ) : (
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Calendar */}
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <button 
                        onClick={prevMonth} 
                        className="w-10 h-10 bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700 rounded-lg flex items-center justify-center transition-colors"
                      >
                        <ChevronLeft className="w-5 h-5 text-gray-700 dark:text-zinc-300" />
                      </button>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">{getMonthName(currentMonth)}</h3>
                      <button 
                        onClick={nextMonth} 
                        className="w-10 h-10 bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700 rounded-lg flex items-center justify-center transition-colors"
                      >
                        <ChevronRight className="w-5 h-5 text-gray-700 dark:text-zinc-300" />
                      </button>
                    </div>

                    <div className="grid grid-cols-7 gap-2 mb-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-center text-sm font-bold text-gray-600 dark:text-zinc-400 h-8 flex items-center justify-center">
                          {day}
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-2">
                      {renderCalendar()}
                    </div>

                    {/* Legend */}
                    <div className="mt-6 flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-700 dark:to-emerald-700 border-2 border-green-300 dark:border-green-600"></div>
                        <span className="text-gray-600 dark:text-zinc-300">Available</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-gradient-to-r from-blue-600 to-purple-600 dark:from-red-500 dark:to-orange-500"></div>
                        <span className="text-gray-600 dark:text-zinc-300">Selected</span>
                      </div>
                    </div>
                  </div>

                  {/* Time Slots */}
                  <div>
                    {selectedDate ? (
                      <div>
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-zinc-800 dark:to-zinc-900 rounded-2xl p-4 mb-6 border-2 border-blue-200 dark:border-red-500/40">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-5 h-5 text-blue-600 dark:text-red-400" />
                            <span className="font-bold text-gray-900 dark:text-white">Selected Date:</span>
                          </div>
                          <p className="text-xl font-bold text-blue-600 dark:text-red-400">
                            {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { 
                              weekday: 'long', 
                              month: 'long', 
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                        </div>

                        <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <Clock className="w-5 h-5 text-purple-600 dark:text-orange-400" />
                          Available Time Slots
                        </h4>
                        <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto custom-scrollbar">
                          {availability[selectedDate]?.map((timeSlot, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleTimeSlotClick(timeSlot)}
                              className="bg-gradient-to-r from-blue-500 to-purple-500 dark:from-red-500 dark:to-orange-500 hover:from-blue-600 hover:to-purple-600 dark:hover:from-red-600 dark:hover:to-orange-600 text-white font-bold py-4 px-4 rounded-xl shadow-md hover:shadow-xl transition-all hover:scale-105"
                            >
                              <Clock className="w-4 h-4 mx-auto mb-1" />
                              {timeSlot}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="h-full flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-red-500/20 dark:to-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Calendar className="w-12 h-12 text-blue-600 dark:text-red-400" />
                          </div>
                          <p className="text-gray-600 dark:text-zinc-300 text-lg">
                            Select a date from the calendar to view available time slots
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.5);
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.7);
        }

        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
}